import { useCallback, useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import L, {
  type Coords,
  type DoneCallback,
  GridLayer,
  type LatLng,
  type GridLayerOptions,
  type LeafletMouseEvent,
  type TileEvent,
} from "leaflet";
import { getYear, getQuarter } from "date-fns";
import * as Sentry from "@sentry/react";
import {
  type TradePriceTileFilter,
  type TradePriceData,
} from "../types/tradePrice";
import { useApiClient } from "@/hooks/useApiClient";
import { type AxiosInstance } from "axios";
import Pbf from "pbf";
import { VectorTile } from "@mapbox/vector-tile";
import { createRoot } from "react-dom/client";
import { TradePricePopupContent } from "./TradePricePopupContent";
import { getTradePriceTileAPI } from "../api/getTradePrice";
import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";
import { useProtomapsPrintAdapter } from "@/features/map/hooks/useProtomapsPrintAdapter";
import type { PrintRenderContext } from "@/features/map/utils/printRenderScale";

interface Props {
  visible: boolean;
  initialZoomLevel: number;
  filter: TradePriceTileFilter;
  onShowDetailList?: (location: {
    prefecture: string;
    municipality: string;
    districtName: string;
  }) => void;
}

export const TRADE_PRICE_MIN_ZOOM = 11;
export const TRADE_PRICE_MAX_ZOOM = 15;
const MIN_ZOOM = TRADE_PRICE_MIN_ZOOM;
const MAX_ZOOM = TRADE_PRICE_MAX_ZOOM;

const LAYER_ATTRIBUTION =
  '<a href="https://www.reinfolib.mlit.go.jp/help/termsOfUse/">不動産情報ライブラリ</a>';

/**
 * タイルキャッシュ
 * VectorTileLayerインスタンスが再作成されてもキャッシュを保持する
 * WARNING: 運用して画面が重くなるなどの問題が出たら、LRUキャッシュなどに変更する
 */
const tileCache = new Map<string, ArrayBuffer>();

interface TradePriceRenderMetrics {
  markerSize: number;
  markerBorderWidth: number;
  lineWidth: number;
  polygonLineWidth: number;
}

export const getTradePriceRenderMetrics = (
  renderScale = 1
): TradePriceRenderMetrics => {
  if (!Number.isFinite(renderScale) || renderScale <= 0) {
    throw new RangeError("renderScale must be a positive finite number");
  }

  return {
    markerSize: 8 * renderScale,
    markerBorderWidth: 4 * renderScale,
    lineWidth: 2 * renderScale,
    polygonLineWidth: renderScale,
  };
};

/**
 * MVTフィーチャーのプロパティ型定義
 */
interface VectorTileFeatureProperties {
  point_in_time_name_ja?: string | number | boolean;
  land_type_name_ja?: string | number | boolean;
  price_information_category_name_ja?: string | number | boolean;
  prefecture_name_ja?: string | number | boolean;
  city_code?: string | number | boolean;
  city_name_ja?: string | number | boolean;
  district_code?: string | number | boolean;
  district_name_ja?: string | number | boolean;
  use_category_name_ja?: string | number | boolean;
  u_transaction_price_total_ja?: string | number | boolean;
  u_transaction_price_unit_price_square_meter_ja?: string | number | boolean;
  u_unit_price_per_tsubo_ja?: string | number | boolean;
  u_area_ja?: string | number | boolean;
  land_shape_name_ja?: string | number | boolean;
  u_land_frontage_ja?: string | number | boolean;
  building_use_name_ja?: string | number | boolean;
  building_structure_name_ja?: string | number | boolean;
  floor_plan_name_ja?: string | number | boolean;
  u_building_total_floor_area_ja?: string | number | boolean;
  u_construction_year_ja?: string | number | boolean;
  front_road_azimuth_name_ja?: string | number | boolean;
  u_front_road_width_ja?: string | number | boolean;
  front_road_type_name_ja?: string | number | boolean;
  land_use_name_ja?: string | number | boolean;
  u_building_coverage_ratio_ja?: string | number | boolean;
  u_floor_area_ratio_ja?: string | number | boolean;
  remark_name_ja?: string | number | boolean;
  future_use_purpose_name_ja?: string | number | boolean;
  remark_renovation_name_ja?: string | number | boolean;
  [key: string]: string | number | boolean | undefined;
}

/**
 * MVTフィーチャーの型定義
 */
interface VectorTileFeature {
  type: number;
  extent: number;
  properties: VectorTileFeatureProperties;
  loadGeometry: () => Array<Array<{ x: number; y: number }>>;
}

interface TileFeature {
  coords: Coords;
  feature: VectorTileFeature;
  tileX: number; // タイル内のピクセル座標
  tileY: number; // タイル内のピクセル座標
}

interface VectorTileLayerOptions extends GridLayerOptions {
  renderScale?: number;
  interactive?: boolean;
}

/**
 * ベクトルタイルをCanvasに描画するカスタムGridLayer
 */
class VectorTileLayer extends GridLayer {
  private readonly apiClient: AxiosInstance;
  private readonly filter: TradePriceTileFilter;
  private readonly interactive: boolean;
  private readonly renderMetrics: TradePriceRenderMetrics;
  private readonly tileFeatures = new Map<string, TileFeature[]>();
  private readonly unloadedTiles = new WeakSet<HTMLElement>();
  private readonly tileFeatureCacheKeys = new WeakMap<HTMLElement, string>();

  constructor(
    apiClient: AxiosInstance,
    filter: TradePriceTileFilter,
    options?: VectorTileLayerOptions
  ) {
    super(options);
    this.apiClient = apiClient;
    this.filter = filter;
    this.interactive = options?.interactive ?? true;
    this.renderMetrics = getTradePriceRenderMetrics(options?.renderScale ?? 1);
    this.on("tileunload", ({ tile }: TileEvent) => {
      this.unloadedTiles.add(tile);
      const tileKey = this.tileFeatureCacheKeys.get(tile);
      if (tileKey !== undefined) {
        this.tileFeatures.delete(tileKey);
      }
    });
  }

  private getTileFeatureCacheKey(coords: Coords): string {
    return `${coords.z}/${coords.x}/${coords.y}`;
  }

  /**
   * クリック位置から最も近いフィーチャーを取得
   */
  public getFeatureAt(latlng: L.LatLng, map: L.Map): VectorTileFeature | null {
    if (this.tileFeatures.size === 0) {
      return null;
    }

    const threshold = 10; // ピクセル単位の許容範囲
    let closestFeature: VectorTileFeature | null = null;
    let minDistance = threshold;

    this.tileFeatures.forEach((features) => {
      for (const { feature, tileX, tileY, coords } of features) {
        // タイルの左上のワールド座標を計算
        const tileSize = this.getTileSize().x;
        const tileOriginPoint = L.point(
          coords.x * tileSize,
          coords.y * tileSize
        );

        // フィーチャーのワールド座標を計算（タイル原点 + タイル内位置）
        const featureWorldPoint = tileOriginPoint.add(L.point(tileX, tileY));

        // ワールド座標から地理座標に変換
        const featureLatLng = map.unproject(featureWorldPoint, coords.z);

        // 現在のズームレベルでスクリーン座標に変換
        const featureScreenPoint = map.latLngToContainerPoint(featureLatLng);
        const clickPoint = map.latLngToContainerPoint(latlng);

        const distance = Math.sqrt(
          Math.pow(clickPoint.x - featureScreenPoint.x, 2) +
            Math.pow(clickPoint.y - featureScreenPoint.y, 2)
        );

        if (distance < minDistance) {
          minDistance = distance;
          closestFeature = feature;
        }
      }
    });

    return closestFeature;
  }

  /**
   * city_codeとdistrict_codeに一致するすべてのフィーチャーを取得
   */
  public getFeaturesByCityAndDistrictCode(
    cityCode: string | number | boolean | undefined,
    districtCode: string | number | boolean | undefined
  ): VectorTileFeature[] {
    const matchingFeatures: VectorTileFeature[] = [];

    this.tileFeatures.forEach((features) => {
      for (const { feature } of features) {
        const properties = feature.properties;
        if (
          properties.city_code === cityCode &&
          properties.district_code === districtCode
        ) {
          matchingFeatures.push(feature);
        }
      }
    });

    return matchingFeatures;
  }

  protected createTile(coords: Coords, done: DoneCallback): HTMLElement {
    const canvas = document.createElement("canvas");
    const size = this.getTileSize();
    canvas.width = size.x;
    canvas.height = size.y;
    this.tileFeatureCacheKeys.set(canvas, this.getTileFeatureCacheKey(coords));

    // 通常表示だけCanvasでクリックイベントをキャプチャする
    canvas.style.pointerEvents = this.interactive ? "auto" : "none";

    (async (): Promise<void> => {
      try {
        const { from, to } = calculatePeriod(this.filter.periodYears);
        const landTypeCode = getPropertyTypeCodes(this.filter.propertyTypes);
        const priceClassification = getPriceClassificationCodes(
          this.filter.priceClassifications
        );

        // キャッシュキーを生成
        const cacheKey = `${coords.z}/${coords.x}/${coords.y}/${from}/${to}/${
          landTypeCode ?? ""
        }/${priceClassification ?? ""}`;

        // グローバルキャッシュから取得
        let data = tileCache.get(cacheKey);

        // キャッシュになければAPIリクエスト
        if (!data) {
          data = await getTradePriceTileAPI(this.apiClient, {
            z: coords.z,
            x: coords.x,
            y: coords.y,
            from,
            to,
            ...(priceClassification && { priceClassification }),
            ...(landTypeCode && { landTypeCode }),
          });

          // グローバルキャッシュに保存
          tileCache.set(cacheKey, data);
        }

        if (data.byteLength > 0) {
          this.renderVectorTile(canvas, data, coords);
        }

        // Leafletのタイル管理を正しく動作させるため、手動で設定
        canvas.classList.add("leaflet-tile-loaded");
        canvas.style.opacity = "1";

        done(undefined, canvas);
      } catch (error) {
        console.error("[TradePriceTileLayer] エラー", error);
        Sentry.captureException(error);
        done(error as Error, canvas);
      }
    })();

    return canvas;
  }

  private renderVectorTile(
    canvas: HTMLCanvasElement,
    data: ArrayBuffer,
    coords: Coords
  ): void {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    try {
      // MVTデータをパース
      const pbf = new Pbf(new Uint8Array(data));
      const tile = new VectorTile(pbf);

      // タイルキーを生成
      const tileKey = this.getTileFeatureCacheKey(coords);
      const features: TileFeature[] = [];

      // すべてのレイヤーを列挙
      const layerNames = Object.keys(tile.layers);

      // 各レイヤーのフィーチャーを描画
      for (const layerName of layerNames) {
        const layer = tile.layers[layerName];

        for (let i = 0; i < layer.length; i++) {
          const feature = layer.feature(i);
          const screenPositions = this.drawFeature(ctx, feature, canvas.width);

          // フィーチャーの位置情報を保存
          for (const pos of screenPositions) {
            features.push({
              coords,
              feature,
              tileX: pos.x,
              tileY: pos.y,
            });
          }
        }
      }

      // 印刷layerではhit testing用のfeature cacheを保持しない
      if (this.interactive && !this.unloadedTiles.has(canvas)) {
        this.tileFeatures.set(tileKey, features);
      }
    } catch (error) {
      console.error("[TradePriceTileLayer] MVTパースエラー", error);
      Sentry.captureException(error);
    }
  }

  private drawFeature(
    ctx: CanvasRenderingContext2D,
    feature: VectorTileFeature,
    tileSize: number
  ): Array<{ x: number; y: number }> {
    const type = feature.type;
    const extent = feature.extent || 4096;
    const scale = tileSize / extent;
    const positions: Array<{ x: number; y: number }> = [];

    // ポイント（点）の場合
    if (type === 1) {
      const geometry = feature.loadGeometry();

      for (const ring of geometry) {
        for (const point of ring) {
          const x = point.x * scale;
          const y = point.y * scale;

          // 位置情報を保存
          positions.push({ x, y });

          // 濃いオレンジの四角でマーカーを描画
          const { markerSize, markerBorderWidth } = this.renderMetrics;
          const halfSize = markerSize / 2;
          const halfBorder = markerBorderWidth / 2;
          // 外枠（白）
          ctx.fillStyle = "white";
          ctx.fillRect(
            x - halfSize - halfBorder,
            y - halfSize - halfBorder,
            markerSize + markerBorderWidth,
            markerSize + markerBorderWidth
          );
          // 内側（濃いオレンジ）
          ctx.fillStyle = "rgba(230, 81, 0, 1)"; // #e65100 (濃いオレンジ)
          ctx.fillRect(x - halfSize, y - halfSize, markerSize, markerSize);
        }
      }
    }
    // ラインストリング（線）の場合
    else if (type === 2) {
      const geometry = feature.loadGeometry();

      ctx.strokeStyle = "rgba(25, 118, 210, 1)";
      ctx.lineWidth = this.renderMetrics.lineWidth;

      for (const ring of geometry) {
        ctx.beginPath();
        for (let i = 0; i < ring.length; i++) {
          const point = ring[i];
          const x = point.x * scale;
          const y = point.y * scale;

          if (i === 0) {
            positions.push({ x, y }); // 最初の点を保存
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      }
    }
    // ポリゴン（面）の場合
    else if (type === 3) {
      const geometry = feature.loadGeometry();

      ctx.fillStyle = "rgba(25, 118, 210, 0.3)";
      ctx.strokeStyle = "rgba(25, 118, 210, 0.7)";
      ctx.lineWidth = this.renderMetrics.polygonLineWidth;

      for (const ring of geometry) {
        ctx.beginPath();
        let sumX = 0;
        let sumY = 0;
        let count = 0;

        for (let i = 0; i < ring.length; i++) {
          const point = ring[i];
          const x = point.x * scale;
          const y = point.y * scale;

          sumX += x;
          sumY += y;
          count++;

          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // 重心を保存
        if (count > 0) {
          positions.push({ x: sumX / count, y: sumY / count });
        }
      }
    }

    return positions;
  }
}

/**
 * MVTプロパティ値を文字列に変換するヘルパー関数
 */
const toStringValue = (
  value: string | number | boolean | undefined
): string => {
  if (value === undefined || value === null) return "";
  return String(value);
};

/**
 * 取引時期を計算する関数
 */
const calculatePeriod = (
  periodYears: 1 | 2 | 5
): { from: string; to: string } => {
  const now = new Date();
  const currentYear = getYear(now);
  const currentQuarter = getQuarter(now);
  const to = `${currentYear}${currentQuarter}`;
  const fromYear = currentYear - periodYears;
  const from = `${fromYear}${currentQuarter}`;
  return { from, to };
};

/**
 * 物件種別コードを取得する関数（複数の種別をカンマ区切りで返す）
 * @returns 物件種別コードのカンマ区切り文字列。配列が空の場合はundefined
 */
const getPropertyTypeCodes = (
  propertyTypes: TradePriceTileFilter["propertyTypes"]
): string | undefined => {
  if (propertyTypes.length === 0) {
    return undefined;
  }

  const codeMap: Record<(typeof propertyTypes)[number], string> = {
    land: "01",
    landWithBuilding: "02",
    usedCondominium: "07",
  };

  return propertyTypes.map((type) => codeMap[type]).join(",");
};

/**
 * 価格情報区分コードを取得する関数（複数の区分をカンマ区切りで返す）
 * @returns 価格情報区分コードのカンマ区切り文字列。配列が空の場合、または両方選択されている場合はundefined
 */
const getPriceClassificationCodes = (
  priceClassifications: TradePriceTileFilter["priceClassifications"]
): string | undefined => {
  // 空、または両方選択されている場合はundefined（全ての区分が対象）
  if (priceClassifications.length === 0 || priceClassifications.length === 2) {
    return undefined;
  }

  const codeMap: Record<(typeof priceClassifications)[number], string> = {
    realEstateTransaction: "01",
    contractPrice: "02",
  };

  return priceClassifications.map((type) => codeMap[type]).join(",");
};

/**
 * 取引価格ベクトルタイルレイヤーのコンポーネント
 * バックエンド経由で国土交通省のバイナリベクトルタイルAPIからデータを取得して表示する
 */
export const TradePriceTileLayer: React.FC<Props> = ({
  visible,
  initialZoomLevel,
  filter,
  onShowDetailList,
}) => {
  const map = useMap();
  const { apiClient } = useApiClient();
  const layerRef = useRef<VectorTileLayer | null>(null);
  const popupRef = useRef<L.Popup | null>(null);
  const popupRootRef = useRef<ReturnType<typeof createRoot> | null>(null);
  const [zoomLevel, setZoomLevel] = useState<number>(initialZoomLevel);
  const createVectorTileLayer = useCallback(
    ({
      renderScale,
      interactive,
      zoomOffset,
    }: {
      renderScale: number;
      interactive: boolean;
      zoomOffset: number;
    }): VectorTileLayer =>
      new VectorTileLayer(apiClient, filter, {
        minZoom: MIN_ZOOM + zoomOffset,
        maxZoom: MAX_ZOOM + zoomOffset,
        opacity: 1,
        updateWhenIdle: false,
        updateWhenZooming: false,
        keepBuffer: 2,
        attribution: LAYER_ATTRIBUTION,
        zIndex: LAYER_Z_INDEX.TRADE_PRICE,
        renderScale,
        interactive,
      }),
    [apiClient, filter]
  );
  const createPrintLayer = useCallback(
    (context: PrintRenderContext): GridLayer =>
      createVectorTileLayer({
        renderScale: context.printScale,
        interactive: false,
        zoomOffset: context.zoomOffset,
      }),
    [createVectorTileLayer]
  );

  useProtomapsPrintAdapter({
    map,
    enabled: visible,
    normalLayerRef: layerRef,
    createPrintLayer,
  });

  // ズームレベルの監視
  useEffect(() => {
    if (!map) return;

    const handleZoomEnd = (): void => {
      setZoomLevel(map.getZoom());
    };

    map.on("zoomend", handleZoomEnd);

    return () => {
      map.off("zoomend", handleZoomEnd);
    };
  }, [map]);

  // マップクリックイベントハンドラー
  useEffect(() => {
    if (!map || !visible) return;

    const handleMouseMove = (e: LeafletMouseEvent): void => {
      if (!layerRef.current) return;

      const feature = layerRef.current.getFeatureAt(e.latlng, map);
      const container = map.getContainer();

      if (feature) {
        container.style.cursor = "pointer";
      } else {
        container.style.cursor = "";
      }
    };

    const handleMapClick = (e: {
      latlng: LatLng;
      originalEvent: MouseEvent;
      displayFeatures?: VectorTileFeature[];
      realEstateTransactionCount?: number;
      contractPriceCount?: number;
    }): void => {
      if (!layerRef.current) return;

      const features = e.displayFeatures ?? [];
      if (features.length === 0) return;

      // 取引価格のフィーチャーが見つかった場合、ポップアップを表示
      if (e.originalEvent) {
        e.originalEvent.preventDefault();
        e.originalEvent.stopPropagation();
      }

      // フィーチャーをTradePriceDataに変換するヘルパー関数
      const convertToTradePriceData = (
        feature: VectorTileFeature
      ): TradePriceData => {
        const properties = feature.properties ?? {};
        return {
          priceCategory: toStringValue(
            properties.price_information_category_name_ja
          ),
          type: toStringValue(properties.land_type_name_ja),
          region: toStringValue(properties.use_category_name_ja),
          municipalityCode: toStringValue(properties.city_code),
          prefecture: toStringValue(properties.prefecture_name_ja),
          municipality: toStringValue(properties.city_name_ja),
          districtName: toStringValue(properties.district_name_ja),
          tradePrice: toStringValue(properties.u_transaction_price_total_ja),
          pricePerUnit: toStringValue(properties.u_unit_price_per_tsubo_ja),
          floorPlan: toStringValue(properties.floor_plan_name_ja),
          area: toStringValue(properties.u_area_ja),
          unitPrice: toStringValue(
            properties.u_transaction_price_unit_price_square_meter_ja
          ),
          landShape: toStringValue(properties.land_shape_name_ja),
          frontage: toStringValue(properties.u_land_frontage_ja),
          totalFloorArea: toStringValue(
            properties.u_building_total_floor_area_ja
          ),
          buildingYear: toStringValue(properties.u_construction_year_ja),
          structure: toStringValue(properties.building_structure_name_ja),
          use: toStringValue(properties.building_use_name_ja),
          purpose: toStringValue(properties.future_use_purpose_name_ja),
          direction: toStringValue(properties.front_road_azimuth_name_ja),
          classification: toStringValue(properties.front_road_type_name_ja),
          breadth: toStringValue(properties.u_front_road_width_ja),
          cityPlanning: toStringValue(properties.land_use_name_ja),
          coverageRatio: toStringValue(properties.u_building_coverage_ratio_ja),
          floorAreaRatio: toStringValue(properties.u_floor_area_ratio_ja),
          period: toStringValue(properties.point_in_time_name_ja),
          renovation: toStringValue(properties.remark_renovation_name_ja),
          remarks: toStringValue(properties.remark_name_ja),
          districtCode: toStringValue(properties.district_code),
        };
      };

      // 不動産取引価格情報と成約価格情報に分けてTradePriceDataに変換
      let realEstateTransactionData: TradePriceData | null = null;
      let contractPriceData: TradePriceData | null = null;

      for (const feature of features) {
        const priceCategory =
          feature.properties.price_information_category_name_ja;
        if (priceCategory === "不動産取引価格情報") {
          realEstateTransactionData = convertToTradePriceData(feature);
        } else if (priceCategory === "成約価格情報") {
          contractPriceData = convertToTradePriceData(feature);
        }
      }

      // 最初のフィーチャーから場所情報を取得
      const firstFeature = features[0];
      const firstProperties = firstFeature.properties ?? {};

      // 既存のポップアップを閉じる
      if (popupRef.current) {
        map.closePopup(popupRef.current);
      }

      // 既存のrootを非同期でアンマウント
      const oldRoot = popupRootRef.current;
      if (oldRoot) {
        setTimeout(() => {
          oldRoot.unmount();
        }, 0);
        popupRootRef.current = null;
      }

      // 新しいポップアップを作成
      const popupHTML = document.createElement("div");
      const root = createRoot(popupHTML);
      popupRootRef.current = root;

      const prefecture = toStringValue(firstProperties.prefecture_name_ja);
      const municipality = toStringValue(firstProperties.city_name_ja);
      const districtName = toStringValue(firstProperties.district_name_ja);

      root.render(
        <TradePricePopupContent
          realEstateTransactionData={realEstateTransactionData}
          contractPriceData={contractPriceData}
          districtName={districtName}
          municipality={municipality}
          prefecture={prefecture}
          realEstateTransactionCount={e.realEstateTransactionCount}
          contractPriceCount={e.contractPriceCount}
          onShowDetailList={
            onShowDetailList
              ? () => {
                  onShowDetailList({ prefecture, municipality, districtName });
                }
              : undefined
          }
        />
      );

      // マップをクリック位置に移動
      map.setView(e.latlng, map.getZoom());

      const popup = L.popup({
        content: popupHTML,
        minWidth: 400,
        maxWidth: 600,
      })
        .setLatLng(e.latlng)
        .openOn(map);

      // ポップアップが閉じられたときにrootをアンマウント
      popup.on("remove", () => {
        const currentRoot = popupRootRef.current;
        if (currentRoot) {
          setTimeout(() => {
            currentRoot.unmount();
          }, 0);
          popupRootRef.current = null;
        }
      });

      popupRef.current = popup;
    };

    // マウスムーブは通常通り登録
    map.on("mousemove", handleMouseMove);

    // クリックイベントはDOM要素に直接登録してキャプチャフェーズで実行
    // これによりLeafletMapのhandleClickより確実に先に実行される
    const mapContainer = map.getContainer();
    const domClickHandler = (e: MouseEvent): void => {
      // LatLngに変換
      const point = map.mouseEventToContainerPoint(e);
      const latlng = map.containerPointToLatLng(point);

      if (!layerRef.current) return;

      const feature = layerRef.current.getFeatureAt(latlng, map);
      if (feature) {
        // city_codeとdistrict_codeを取得
        const cityCode = feature.properties.city_code;
        const districtCode = feature.properties.district_code;

        // 同じ市区町村のすべてのフィーチャーを取得
        const matchingFeatures =
          layerRef.current.getFeaturesByCityAndDistrictCode(
            cityCode,
            districtCode
          );

        // price_information_category_name_jaでグループ化
        const realEstateTransactions = matchingFeatures.filter(
          (f) =>
            f.properties.price_information_category_name_ja ===
            "不動産取引価格情報"
        );
        const contractPrices = matchingFeatures.filter(
          (f) =>
            f.properties.price_information_category_name_ja === "成約価格情報"
        );

        // point_in_time_name_jaで最新を判定（降順ソート）
        const sortByPointInTime = (
          features: VectorTileFeature[]
        ): VectorTileFeature[] => {
          return features.sort((a, b) => {
            const aTime = String(a.properties.point_in_time_name_ja ?? "");
            const bTime = String(b.properties.point_in_time_name_ja ?? "");
            return bTime.localeCompare(aTime);
          });
        };

        const latestRealEstateTransaction = sortByPointInTime(
          realEstateTransactions
        )[0];
        const latestContractPrice = sortByPointInTime(contractPrices)[0];

        // ポップアップに表示するデータを作成（最新のものだけ）
        const displayFeatures: VectorTileFeature[] = [];
        if (latestRealEstateTransaction) {
          displayFeatures.push(latestRealEstateTransaction);
        }
        if (latestContractPrice) {
          displayFeatures.push(latestContractPrice);
        }

        // フィーチャーが見つかった場合、イベントを停止
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleMapClick({
          latlng,
          originalEvent: e,
          displayFeatures,
          realEstateTransactionCount: realEstateTransactions.length,
          contractPriceCount: contractPrices.length,
        });
      }
    };

    // キャプチャフェーズで登録（第3引数をtrue）
    mapContainer.addEventListener("click", domClickHandler, true);

    return () => {
      map.off("mousemove", handleMouseMove);
      mapContainer.removeEventListener("click", domClickHandler, true);
      // カーソルをリセット
      map.getContainer().style.cursor = "";
      // rootをクリーンアップ（非同期）
      const currentRoot = popupRootRef.current;
      if (currentRoot) {
        setTimeout(() => {
          currentRoot.unmount();
        }, 0);
        popupRootRef.current = null;
      }
    };
  }, [map, visible]);

  // 通常表示用layerのinstanceはzoom変更では作り直さず、filter/client変更時だけ交換する
  useEffect(() => {
    if (!map) return;

    let layer: VectorTileLayer | null = null;

    try {
      layer = createVectorTileLayer({
        renderScale: 1,
        interactive: true,
        zoomOffset: 0,
      });
      layerRef.current = layer;
    } catch (error) {
      console.error("[TradePriceTileLayer] レイヤー作成エラー", error);
      Sentry.captureException(error);
    }

    return () => {
      if (layer && map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
      if (layerRef.current === layer) {
        layerRef.current = null;
      }
    };
  }, [map, createVectorTileLayer]);

  // visibleとzoom範囲に応じて、同じ通常layer instanceをmapへ出し入れする
  useEffect(() => {
    if (!map) return;
    if (map.getContainer().hasAttribute("data-printing")) return;

    const layer = layerRef.current;
    if (layer) {
      const shouldShowLayer =
        visible && zoomLevel >= MIN_ZOOM && zoomLevel <= MAX_ZOOM;

      if (shouldShowLayer && !map.hasLayer(layer)) {
        layer.addTo(map);

        // z-indexを設定
        const container = layer.getContainer();
        if (container) {
          container.style.zIndex = String(LAYER_Z_INDEX.TRADE_PRICE);
        }
      } else if (!shouldShowLayer && map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    }

    return () => {
      if (popupRef.current && map) {
        map.closePopup(popupRef.current);
        popupRef.current = null;
      }
      const cleanupRoot = popupRootRef.current;
      if (cleanupRoot) {
        setTimeout(() => {
          cleanupRoot.unmount();
        }, 0);
        popupRootRef.current = null;
      }
    };
  }, [map, visible, zoomLevel, createVectorTileLayer]);

  // フィルタが変更されたら、既存レイヤーを再描画
  useEffect(() => {
    if (layerRef.current && visible) {
      // GridLayerの内部タイルキャッシュをクリア
      (
        layerRef.current as VectorTileLayer & {
          _tiles: Record<string, unknown>;
        }
      )._tiles = {};
      layerRef.current.redraw();
    }
  }, [filter, visible]);

  return null;
};
